import * as Sentry from "@sentry/react";
import { Auth } from "aws-amplify";
import axios, { type AxiosInstance, type AxiosRequestConfig } from "axios";

import { Path } from "~/v2/path";
import { withAppBasePath } from "~/v2/utils/appBasePath";
import { getAccessToken, getOptionalAccessToken } from "./authxToken";
import {
  type AuthRedirectReason,
  MustReauthenticateError,
} from "./MustReauthenticateError";

let logoutPromise: Promise<void> | null = null;

export const shouldRedirectForAuthFailure = (err: unknown): boolean => {
  return err instanceof MustReauthenticateError;
};

const getUnauthorizedErrorMetadata = (err: unknown) => ({
  code: axios.isAxiosError(err) ? err.code : undefined,
  source: "api",
  status: axios.isAxiosError(err) ? err.response?.status : undefined,
});

const getApiErrorMetadata = (err: unknown) => {
  if (!axios.isAxiosError(err)) {
    return undefined;
  }

  return {
    baseURL: err.config?.baseURL,
    code: err.code,
    method: err.config?.method,
    source: "api",
    status: err.response?.status,
    url: err.config?.url,
  };
};

const isSuppressed401 = (err: unknown): boolean =>
  axios.isAxiosError(err) &&
  err.response?.status === 401 &&
  err.config?.logoutOn401 !== true;

const captureApiFailure = (err: unknown): void => {
  const metadata = getApiErrorMetadata(err);
  if (!metadata) {
    return;
  }

  const suppressed401 = isSuppressed401(err);

  Sentry.captureException(new Error("API request failed"), {
    level: "warning",
    tags: {
      component: "api",
      method: metadata.method,
      status: metadata.status,
      ...(suppressed401 && { suppressed401: true }),
    },
    // Collapse a chronic per-endpoint 401 storm into a single issue instead of
    // flooding Sentry once these no longer force a logout.
    fingerprint: suppressed401
      ? ["api-suppressed-401", metadata.method ?? "", metadata.url ?? ""]
      : undefined,
    extra: metadata,
  });
};

export const getAuthRedirectReason = (err: unknown): AuthRedirectReason => {
  if (!(err instanceof MustReauthenticateError)) {
    return "reauthentication_required";
  }

  return err.redirectReason;
};

const captureLoginRedirect = ({
  redirectPath,
  redirectReason,
}: {
  redirectPath: string;
  redirectReason: AuthRedirectReason;
}): void => {
  Sentry.captureMessage("User redirected to login", {
    level: "info",
    tags: {
      action: "login_redirect",
      component: "auth",
      reason: redirectReason,
    },
    extra: {
      currentPath: window.location.pathname,
      redirectPath,
    },
  });
};

export const logoutAndRedirectToLogin = ({
  redirectPath = Path.ROUTE_LOGIN,
  redirectReason = "manual_logout",
  signOutOptions,
}: {
  redirectPath?: string;
  redirectReason?: AuthRedirectReason;
  signOutOptions?: { global?: boolean };
} = {}): Promise<void> => {
  if (logoutPromise) {
    return logoutPromise;
  }

  logoutPromise = (async () => {
    captureLoginRedirect({ redirectPath, redirectReason });

    try {
      await Auth.signOut(signOutOptions);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: "signOut" },
        extra: { opts: signOutOptions },
      });
    } finally {
      window.location.replace(withAppBasePath(redirectPath));
    }
  })();

  return logoutPromise;
};

export const handleAuthFailure = async (err: unknown): Promise<boolean> => {
  if (!shouldRedirectForAuthFailure(err)) {
    return false;
  }

  await logoutAndRedirectToLogin({
    redirectPath: Path.ROUTE_LOGIN,
    redirectReason: getAuthRedirectReason(err),
    signOutOptions: { global: false },
  });
  return true;
};

export const getAccessTokenOrRedirect = async (): Promise<string> => {
  try {
    return await getAccessToken();
  } catch (err) {
    await handleAuthFailure(err);
    throw err;
  }
};

export const attachAuthInterceptors = (
  instance: AxiosInstance,
): AxiosInstance => {
  instance.interceptors.request.use(async (config: AxiosRequestConfig) => {
    const token = await getAccessTokenOrRedirect();
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    async (err) => {
      captureApiFailure(err);

      if (
        axios.isAxiosError(err) &&
        err.response?.status === 401 &&
        err.config?.logoutOn401 === true
      ) {
        const authError = new MustReauthenticateError(
          "Unauthorized",
          "api_401",
          getUnauthorizedErrorMetadata(err),
        );
        await handleAuthFailure(authError);
        return Promise.reject(authError);
      }

      return Promise.reject(err);
    },
  );

  return instance;
};

export const attachOptionalAuthInterceptor = (
  instance: AxiosInstance,
): AxiosInstance => {
  instance.interceptors.request.use(async (config: AxiosRequestConfig) => {
    const token = await getOptionalAccessToken();
    if (token) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  instance.interceptors.response.use(
    (response) => response,
    (err) => {
      captureApiFailure(err);
      return Promise.reject(err);
    },
  );

  return instance;
};
